/**
 * @file 加入知识库弹窗 — UseFormSearch 自定义字段
 * 视觉还原优化前：自绘 label(80px)+field，避开 tea Form.Item label 错位 / 挡点击
 */
import React, { useEffect, useRef } from 'react';
import classNames from 'classnames';
import MarkdownIt from 'markdown-it';
import DOMPurify from 'dompurify';
import CommonBubble from '@home/components/CommonBubble';
import { LANGUAGE_EN } from '@home/_config/config';
import {
  Dropdown,
  Icon,
  List,
  Radio,
  RadioGroup,
  Switch,
  TextArea,
} from 'tea-component';
import { useIntl } from 'umi';
import { isEditableHtmlEmpty, normalizeEditableFormValue } from '../utils';

export type TKnowledgeSpace = 'personal' | 'team';

/** 统一表单行：与优化前 dba-kb-modal__row / __label / __field 一致 */
export const KbFormRow: React.FC<{
  label: React.ReactNode;
  required?: boolean;
  textarea?: boolean;
  children: React.ReactNode;
}> = ({ label, required = false, textarea = false, children }) => (
  <div
    className={classNames('dba-kb-modal__row', {
      'dba-kb-modal__row--textarea': textarea,
    })}
  >
    <label className="dba-kb-modal__label">
      {label}
      {required ? <span className="dba-kb-modal__required">*</span> : null}
    </label>
    <div className="dba-kb-modal__field">{children}</div>
  </div>
);

export interface IKbSpaceFieldProps {
  /** final-form 注入，勿当作 Radio 的 name */
  name?: string;
  value?: TKnowledgeSpace;
  onChange?: (value: TKnowledgeSpace) => void;
  onBlur?: (...args: unknown[]) => void;
  onFocus?: (...args: unknown[]) => void;
  onSpaceChange?: (value: TKnowledgeSpace) => void;
  /** 团队空间列表是否为空（无可用团队），为 true 时禁用团队 Radio 并展示创建提示 */
  hasTeamSpaces?: boolean;
}

/** 选择空间：个人 / 团队 */
export const KbSpaceField: React.FC<IKbSpaceFieldProps> = ({
  value,
  onChange,
  onSpaceChange,
  hasTeamSpaces = true,
}) => {
  const { formatMessage, locale } = useIntl();
  const isEN = locale === LANGUAGE_EN;
  // final-form 空值是 ''；空串不能用 || 默认，否则点选会被盖回 personal
  const current: TKnowledgeSpace =
    value === 'team' || value === 'personal' ? value : 'personal';

  const handleChange = (val: string): void => {
    const next = (val === 'team' ? 'team' : 'personal') as TKnowledgeSpace;
    onChange?.(next);
    onSpaceChange?.(next);
  };

  const teamRadio = (
    <Radio
      name="team"
      className="dba-kb-modal__radio"
      disabled={!hasTeamSpaces}
    >
      {formatMessage({ id: 'dba_kb_add_space_team' })}
    </Radio>
  );

  return (
    <KbFormRow label={formatMessage({ id: 'dba_kb_add_space' })}>
      <RadioGroup
        className="dba-kb-modal__space-radio-group"
        value={current}
        onChange={handleChange}
      >
        <span className="dba-kb-modal__radio-item">
          <Radio name="personal" className="dba-kb-modal__radio">
            {formatMessage({ id: 'dba_kb_add_space_personal' })}
          </Radio>
        </span>
        <span className="dba-kb-modal__radio-item">
          {hasTeamSpaces ? teamRadio : (
            <CommonBubble
              trigger="hover"
              placement="top"
              dark
              overlayClassName="dba-kb-modal__team-space-bubble"
              content={(
                <div className="dba-kb-modal__team-space-bubble-content">
                  {formatMessage({ id: 'dba_kb_add_space_team_disabled_tooltip' })}
                  <a
                    className="dba-kb-modal__team-space-bubble-link"
                    href="/gamewiki/kb/platform/knowledge-bases?action=create-space"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {formatMessage({ id: 'dba_kb_add_space_team_disabled_link' })}
                  </a>
                  {isEN ? formatMessage({ id: 'dba_kb_add_space_team_disabled_suffix' }) : null}
                </div>
              )}
            >
              <span className="dba-kb-modal__radio-bubble-wrap">{teamRadio}</span>
            </CommonBubble>
          )}
        </span>
      </RadioGroup>
    </KbFormRow>
  );
};

export interface IKbSelectFieldProps {
  name?: string;
  value?: string;
  onChange?: (value: string) => void;
  onBlur?: (...args: unknown[]) => void;
  onFocus?: (...args: unknown[]) => void;
  label: React.ReactNode;
  required?: boolean;
  placeholder?: string;
  options: Array<{ value: string; text: string; }>;
  /** 值为空或不在 options 内时，自动选第一个 */
  autoSelectFirst?: boolean;
}

/** 下拉选择：对齐 Gallery — overlay 抬高 z-index + closeDropdown，避免被弹窗挡住 / 被 contentEditable 抢焦点 */
export const KbSelectField: React.FC<IKbSelectFieldProps> = ({
  value = '',
  onChange,
  label,
  required,
  placeholder,
  options,
  autoSelectFirst = false,
}) => {
  const optionsKey = options.map((item) => `${item.value}\0${item.text}`).join('\n');
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    if (!autoSelectFirst || options.length === 0) return;
    if (value && options.some((item) => item.value === value)) return;

    const firstValue = options[0].value;
    const timer = window.setTimeout(() => {
      onChangeRef.current?.(firstValue);
    }, 0);

    return () => clearTimeout(timer);
  }, [autoSelectFirst, optionsKey, value, options]);

  const selected = options.find((item) => item.value === value);

  return (
    <KbFormRow label={label} required={required}>
      <Dropdown
        trigger="click"
        appearance="pure"
        matchButtonWidth
        overlayClassName="dba-kb-modal__select-overlay"
        button={(
          <button type="button" className="dba-kb-modal__select-trigger">
            <span
              className={
                value
                  ? 'dba-kb-modal__select-text'
                  : 'dba-kb-modal__select-placeholder'
              }
            >
              {selected?.text || placeholder}
            </span>
            <Icon type="arrowdown" />
          </button>
        )}
      >
        {(closeDropdown) => (
          <List type="option">
            {options.map((item) => (
              <List.Item
                key={item.value}
                selected={value === item.value}
                // 阻止 mousedown 抢走 contentEditable 焦点，否则 blur 回写会打断 click
                onMouseDown={(e: React.MouseEvent) => e.preventDefault()}
                onClick={() => {
                  onChange?.(item.value);
                  closeDropdown();
                }}
              >
                {item.text}
              </List.Item>
            ))}
          </List>
        )}
      </Dropdown>
    </KbFormRow>
  );
};

/**
 * 团队选择下拉（扩展 KbSelectField，切换时通知外部刷新知识库列表）
 */
export const KbTeamSelectField: React.FC<IKbSelectFieldProps & { onTeamChange?: (teamId: string) => void; }> = ({
  onTeamChange,
  onChange,
  ...rest
}) => {
  const handleChange = (val: string): void => {
    onChange?.(val);
    onTeamChange?.(val);
  };

  return <KbSelectField {...rest} onChange={handleChange} />;
};

export interface IKbAiToggleFieldProps {
  name?: string;
  value?: boolean;
  onChange?: (value: boolean) => void;
  onBlur?: (...args: unknown[]) => void;
  onFocus?: (...args: unknown[]) => void;
}

/** 知识 AI 智能整理开关 + 说明 */
export const KbAiToggleField: React.FC<IKbAiToggleFieldProps> = ({
  value = false,
  onChange,
}) => {
  const { formatMessage } = useIntl();

  return (
    <KbFormRow label={formatMessage({ id: 'dba_kb_add_ai_toggle' })}>
      <div className="dba-kb-modal__field--switch">
        <Switch value={!!value} onChange={(val: boolean) => onChange?.(val)} />
      </div>
    </KbFormRow>
  );
};

export interface IKbTextInputProps {
  name?: string;
  value?: string;
  onChange?: (value: string) => void;
  onBlur?: (...args: unknown[]) => void;
  onFocus?: (...args: unknown[]) => void;
  label?: React.ReactNode;
  required?: boolean;
  placeholder?: string;
}

/** 标题 / 知识库名称 */
export const KbTextInput: React.FC<IKbTextInputProps> = ({
  value = '',
  onChange,
  label,
  required,
  placeholder,
}) => {
  const input = (
    <input
      className="dba-kb-modal__input"
      type="text"
      value={value ?? ''}
      onChange={(e) => onChange?.(e.target.value)}
      placeholder={placeholder}
    />
  );

  if (!label) return input;

  return (
    <KbFormRow label={label} required={required}>
      {input}
    </KbFormRow>
  );
};

export interface IKbTextAreaFieldProps {
  name?: string;
  value?: string;
  onChange?: (value: string) => void;
  onBlur?: (...args: unknown[]) => void;
  onFocus?: (...args: unknown[]) => void;
  label: React.ReactNode;
  required?: boolean;
  placeholder?: string;
  rows?: number;
  short?: boolean;
}

/** 正文 / 描述 */
export const KbTextAreaField: React.FC<IKbTextAreaFieldProps> = ({
  value = '',
  onChange,
  label,
  required,
  placeholder,
  rows = 8,
  short = false,
}) => (
  <KbFormRow label={label} required={required} textarea>
    <TextArea
      className={classNames('dba-kb-modal__textarea', {
        'dba-kb-modal__textarea--short': short,
      })}
      value={value ?? ''}
      onChange={(val: string) => onChange?.(val)}
      placeholder={placeholder}
      rows={rows}
    />
  </KbFormRow>
);

export interface IKbContentEditableFieldProps {
  name?: string;
  value?: string;
  onChange?: (value: string) => void;
  onBlur?: (...args: unknown[]) => void;
  onFocus?: (...args: unknown[]) => void;
  label: React.ReactNode;
  required?: boolean;
  placeholder?: string;
  /** 划词带来的 HTML，优先于 markdown 渲染，便于保真预览并可直接改 */
  initialHtml?: string;
}

const mdRenderer = new MarkdownIt({
  html: false,
  linkify: true,
  breaks: true,
});

/**
 * 正文：Markdown/HTML 预览 + contentEditable 原地编辑
 * - 展示：initialHtml 优先，否则 markdown-it 渲染 value
 * - 回写表单：onInput/onBlur 归一化后 onChange（空内容写 ''，避免 <br> 绕过校验）
 */
export const KbContentEditableField: React.FC<IKbContentEditableFieldProps> = ({
  value = '',
  onChange,
  onBlur,
  label,
  required,
  placeholder,
  initialHtml = '',
}) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const lastEmittedRef = useRef<string | null>(null);

  const emitValue = () => {
    const el = editorRef.current;
    if (!el) return;
    const next = normalizeEditableFormValue(el.innerHTML || '');
    if (next === lastEmittedRef.current) return;
    lastEmittedRef.current = next;
    if (!next && el.innerHTML) {
      el.innerHTML = '';
    }
    onChange?.(next);
  };

  // 外部预填 / 表单 reset 时灌入；编辑中不重灌，避免丢光标
  useEffect(() => {
    const el = editorRef.current;
    if (!el || document.activeElement === el) return;

    const next = value ?? '';
    if (lastEmittedRef.current === next && el.childNodes.length > 0) return;

    lastEmittedRef.current = next;
    if (initialHtml.trim()) {
      el.innerHTML = DOMPurify.sanitize(initialHtml);
    } else if (next.trim()) {
      el.innerHTML = DOMPurify.sanitize(mdRenderer.render(next));
    } else {
      el.innerHTML = '';
    }
  }, [value, initialHtml]);

  const isEmpty = isEditableHtmlEmpty(value ?? '');

  return (
    <KbFormRow label={label} required={required} textarea>
      <div
        ref={editorRef}
        className={classNames(
          'dba-kb-modal__content-editable',
          'dba-mainContent',
          'markdown-body',
          { 'dba-kb-modal__content-editable--empty': isEmpty },
        )}
        contentEditable
        suppressContentEditableWarning
        data-placeholder={placeholder}
        onInput={emitValue}
        onBlur={(e) => {
          emitValue();
          onBlur?.(e);
        }}
      />
    </KbFormRow>
  );
};
